import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Wifi, Coffee, Dumbbell, ShieldCheck, MapPin, Users, CalendarDays, Music, Gamepad2, Check, Zap } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";

// ─── URBAN HERO ───
export function UrbanHero() {
  const { getPublishedPageContent } = useTheme();
  const content = getPublishedPageContent('home', 'hero');
  const c = {
    subheading: "Live. Work. Thrive.",
    heading: "Urban Living, Redefined.",
    description: "Fully-furnished apartments and co-living spaces in the heart of the city. Flexible leases, vibrant community.",
    buttonText: "Browse Spaces",
    buttonLink: "/spaces",
    buttonText2: "Our Community",
    buttonLink2: "/community",
    imageUrl: "https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=1920&q=80",
    stats: [{ value: "200+", label: "Rooms" }, { value: "12", label: "Locations" }, { value: "4.8★", label: "Rating" }],
    ...content,
  };
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-primary">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 right-20 w-96 h-96 border border-accent rounded-full" />
        <div className="absolute bottom-20 left-20 w-64 h-64 border border-accent rotate-45" />
      </div>
      <div className="max-w-[1400px] mx-auto px-6 py-20 relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-accent text-accent-foreground rounded-full text-xs font-bold uppercase mb-6">
            <Zap className="w-3 h-3" /> {c.subheading}
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-primary-foreground leading-tight mb-6">{c.heading}</h1>
          <p className="text-primary-foreground/70 text-lg mb-8 max-w-lg">{c.description}</p>
          <div className="flex flex-wrap gap-3 mb-10">
            <Link to={c.buttonLink || "/spaces"} className="inline-flex items-center gap-2 px-8 py-4 bg-accent text-accent-foreground font-bold text-sm uppercase rounded-xl hover:bg-accent/90 transition-all">
              {c.buttonText} <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to={c.buttonLink2 || "/community"} className="inline-flex items-center gap-2 px-8 py-4 border-2 border-primary-foreground/20 text-primary-foreground font-bold text-sm uppercase rounded-xl hover:bg-primary-foreground/10 transition-all">
              {c.buttonText2 || "Our Community"}
            </Link>
          </div>
          <div className="flex gap-8">
            {(c.stats || []).map((s: any) => (
              <div key={s.label}><div className="text-2xl font-bold text-accent">{s.value}</div><div className="text-primary-foreground/50 text-xs">{s.label}</div></div>
            ))}
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.3 }}
          className="hidden lg:block">
          <img src={c.imageUrl} alt="Urban Space" className="rounded-2xl w-full h-[500px] object-cover" />
        </motion.div>
      </div>
    </section>
  );
}

// ─── URBAN AVAILABLE SPACES ───
export function UrbanAvailableSpaces() {
  const { getPublishedPageContent } = useTheme();
  const content = getPublishedPageContent('home', 'available-spaces');
  const c = {
    heading: "Find Your Space",
    description: "From private studios to shared living.",
    items: [
      { title: "Private Studio", description: "₹18,000/mo · 250 sq.ft · Private bathroom, Kitchenette, Balcony", imageUrl: "https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=500&q=80" },
      { title: "Shared Room", description: "₹8,000/mo · 150 sq.ft · 2-sharing, Common bath, All utilities", imageUrl: "https://images.unsplash.com/photo-1555854877-bab0e564b8d5?w=500&q=80" },
      { title: "1BHK Apartment", description: "₹25,000/mo · 450 sq.ft · Full kitchen, Living room, Smart lock", imageUrl: "https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=500&q=80" },
    ],
    ...content,
  };
  return (
    <section className="py-20 bg-secondary">
      <div className="max-w-[1400px] mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{c.heading}</h2>
          <p className="text-muted-foreground">{c.description}</p>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {(c.items || []).map((s: any, i: number) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.15 }}
              className="bg-card rounded-2xl overflow-hidden shadow-md group hover:shadow-xl transition-shadow">
              <div className="relative h-56 overflow-hidden">
                <img src={s.imageUrl || '/placeholder.svg'} alt={s.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-6">
                <h3 className="font-bold text-xl mb-1">{s.title}</h3>
                <p className="text-muted-foreground text-sm">{s.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── URBAN AMENITIES GRID ───
export function UrbanAmenitiesGrid() {
  const { getPublishedPageContent } = useTheme();
  const content = getPublishedPageContent('home', 'amenities-grid');
  const c = {
    heading: "Everything You Need",
    description: "Curated amenities for modern city living.",
    items: [
      { title: "High-Speed WiFi", description: "Lightning-fast internet" },
      { title: "Café & Lounge", description: "On-site café" },
      { title: "Fitness Center", description: "Fully equipped gym" },
      { title: "24/7 Security", description: "Round-the-clock security" },
      { title: "Prime Locations", description: "Central city locations" },
      { title: "Co-working Space", description: "Dedicated work areas" },
      { title: "Game Room", description: "Entertainment and recreation" },
      { title: "Rooftop Terrace", description: "Panoramic city views" },
    ],
    ...content,
  };
  const defaultIcons = [Wifi, Coffee, Dumbbell, ShieldCheck, MapPin, Users, Gamepad2, Music];
  return (
    <section className="py-20 bg-card">
      <div className="max-w-[1400px] mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{c.heading}</h2>
          {c.description && <p className="text-muted-foreground">{c.description}</p>}
        </motion.div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {(c.items || []).map((a: any, i: number) => {
            const Icon = defaultIcons[i % defaultIcons.length];
            return (
              <motion.div key={i} initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                className="flex flex-col items-center gap-3 p-6 rounded-2xl hover:bg-secondary transition-colors cursor-pointer group">
                <div className="w-16 h-16 rounded-full bg-accent/10 group-hover:bg-accent flex items-center justify-center transition-all">
                  <Icon className="w-7 h-7 text-accent group-hover:text-accent-foreground transition-colors" />
                </div>
                <span className="font-semibold text-sm text-center">{a.title}</span>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─── URBAN COMMUNITY EVENTS ───
export function UrbanCommunityEvents() {
  const { getPublishedPageContent } = useTheme();
  const content = getPublishedPageContent('home', 'community-events');
  const c = {
    heading: "Never a Dull Moment",
    description: "Weekly events, networking nights, and more.",
    items: [
      { title: "Rooftop Movie Night", description: "FRI 21 · 7:30 PM · Social" },
      { title: "Startup Pitch Day", description: "SAT 22 · 2:00 PM · Networking" },
      { title: "Sunday Brunch", description: "SUN 23 · 11:00 AM · Food" },
      { title: "Yoga & Meditation", description: "WED 26 · 6:30 AM · Wellness" },
    ],
    ...content,
  };
  return (
    <section className="py-20 bg-secondary">
      <div className="max-w-[1400px] mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{c.heading}</h2>
          <p className="text-muted-foreground">{c.description}</p>
        </motion.div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {(c.items || []).map((e: any, i: number) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="bg-card rounded-2xl p-6 hover:shadow-lg transition-shadow cursor-pointer group">
              <h3 className="font-bold mt-1">{e.title}</h3>
              <p className="text-muted-foreground text-sm flex items-center gap-1 mt-1"><CalendarDays className="w-3 h-3" />{e.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── URBAN PRICING ───
export function UrbanPricing() {
  const { getPublishedPageContent } = useTheme();
  const content = getPublishedPageContent('home', 'pricing');
  const c = {
    heading: "Simple, Transparent Pricing",
    description: "All-inclusive. No hidden charges.",
    items: [
      { title: "Shared — ₹8,000/mo", description: "2-sharing room, Common areas, Basic WiFi, Housekeeping" },
      { title: "Private Studio — ₹18,000/mo", description: "Private room, Attached bath, High-speed WiFi, All utilities, Events access" },
      { title: "1BHK Premium — ₹25,000/mo", description: "Full apartment, Kitchen, Smart lock, Premium WiFi, Events access, Parking" },
    ],
    ...content,
  };
  return (
    <section className="py-20 bg-primary text-primary-foreground">
      <div className="max-w-[1400px] mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{c.heading}</h2>
          <p className="text-primary-foreground/60">{c.description}</p>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          {(c.items || []).map((p: any, i: number) => {
            const popular = i === 1;
            const features = p.description.split(', ');
            return (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.15 }}
                className={`rounded-2xl p-8 ${popular ? 'bg-accent text-accent-foreground ring-2 ring-accent scale-105' : 'bg-primary-foreground/5 border border-primary-foreground/10'}`}>
                {popular && <span className="text-xs font-bold uppercase mb-4 block">Most Popular</span>}
                <h3 className="text-xl font-bold mb-6">{p.title}</h3>
                <ul className="space-y-3 mb-8">
                  {features.map((f: string, j: number) => (
                    <li key={j} className="flex items-center gap-2 text-sm"><Check className="w-4 h-4 flex-shrink-0" />{f.trim()}</li>
                  ))}
                </ul>
                <button className={`w-full py-3 rounded-xl font-bold text-sm uppercase ${popular ? 'bg-primary text-primary-foreground' : 'bg-accent text-accent-foreground'} hover:opacity-90 transition-opacity`}>
                  Book Now
                </button>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
